import _ from "lodash";
import Constants from "../../constants";
import { Item } from "../../models/item";
import TranslationMap from "../../models/translationMap";
import { ActiveFilterMap, ActiveFilterValues, Filter, FilterTypeMap, FilterValue } from "./filter";

function getUncategorizedName(key: string) {
    // TODO: translate both OTHER and key.
    return `OTHER: ${key}`;
}

export class ItemFilter {
    private filter: ActiveFilterMap;

    private locale: string;

    constructor(locale: string, values?: ActiveFilterMap | undefined) {
        this.locale = locale;
        this.filter = {};
        if (values) {
            this.filter = _.cloneDeep(values);
        }
    }

    // Need to get dynamic type based on items
    getFilterTypesFromMenuItems = (enabledFilterTypes: FilterTypeMap, menuItems: Item[]): string[] => {
        const dynamicType = menuItems.find(item => item.dynamicType)?.dynamicType || Constants.genericFilterKey;
        const enabledTypes = enabledFilterTypes[dynamicType];
        if (!enabledTypes) return [];

        const uniqueItemFilterTypes = new Set<string>();
        menuItems.forEach(item => {
            const { filterValues } = item;
            if (filterValues) {
                Object.keys(filterValues!).forEach(filterType => {
                    uniqueItemFilterTypes.add(filterType.toUpperCase());
                });
            }
        });

        return enabledTypes.filter(filterType => uniqueItemFilterTypes.has(filterType.toUpperCase()));
    };

    getFilterValuesFromMenuItems = (types: string[], menuItems: Item[], guiTranslations: TranslationMap) => {
        if (types.length === 0) {
            return [];
        }

        const filters = types.reduce((collection: Filter[], filterType: string) => {
            const addedValues = new Set<string>();
            const filterValues: FilterValue[] = [];
            const addValue = (value: string) => {
                const valUpper = value.toUpperCase();
                if (!addedValues.has(valUpper)) {
                    addedValues.add(valUpper);
                    const displayName = guiTranslations.get(value);
                    filterValues.push({ value, displayName, productCount: 0 });
                }
            };
            menuItems.forEach((item: Item) => {
                // The non-selection furniture sentinel ("__no_item") is a selectable but value-less
                // tile; skip it so it never creates a spurious "OTHER: <filter>" value. Match the
                // exact sentinel, not a "__" prefix — real products also use that prefix.
                if (item.articleNumber === Constants.nonSelectionArticleNumber) {
                    return;
                }
                const value = item.filterValues?.[filterType];
                if (value) {
                    this.splitFilterValues(value).forEach(val => addValue(val));
                } else {
                    addValue(getUncategorizedName(filterType));
                }
            });

            collection.push({ name: filterType, values: filterValues });
            return collection;
        }, []);

        return filters;
    };

    countAllFilterValues(filters: Filter[], filteredItems: Item[]): Filter[] {
        return filters.map((filter: Filter) => {
            const currentFilterValues: { [key: string]: FilterValue } = {};
            const otherName = getUncategorizedName(filter.name);
            let addOthers = false;

            // Add all filter values
            filter.values.forEach(f => {
                if (f.value !== otherName) {
                    const valueUpper = f.value.toUpperCase();
                    currentFilterValues[valueUpper] = { value: f.value, displayName: f.displayName, productCount: 0 };
                } else {
                    addOthers = true;
                }
            });

            let numOthers = 0;
            filteredItems.forEach((item: Item) => {
                // See getFilterValuesFromMenuItems: skip the non-selection sentinel entirely (before
                // any count path) so it is never tallied into "OTHER: <filter>".
                if (item.articleNumber === Constants.nonSelectionArticleNumber) {
                    return;
                }
                if (!item.filterValues) {
                    numOthers += 1;
                    return;
                }

                const isAvailable = this.filterItem(item, filter.name);
                if (!isAvailable) {
                    return;
                }

                const itemValue = item.filterValues[filter.name];
                if (itemValue) {
                    this.splitFilterValues(itemValue).forEach(val => {
                        const valueUpper = val.toUpperCase();
                        const filterValueObject = currentFilterValues[valueUpper];
                        if (filterValueObject) {
                            filterValueObject.productCount += 1;
                        }
                    });
                } else {
                    // if there is a category, but the item does not have any filterdata on that category,
                    // add it to a Other: [category]
                    numOthers += 1;
                }
            });

            const values = Object.values(currentFilterValues);
            values.sort((a, b) =>
                a.displayName.localeCompare(b.displayName, this.locale, { sensitivity: "base", numeric: true })
            );
            if (addOthers) {
                values.push({ value: otherName, displayName: otherName, productCount: numOthers });
            }
            return { name: filter.name, values };
        });
    }

    filterValues(
        menuItems: Item[],
        filteredItems: Item[],
        filterTypes: FilterTypeMap,
        guiTranslation: TranslationMap
    ): Filter[] {
        const filterCategories = this.getFilterTypesFromMenuItems(filterTypes, menuItems);
        const filters = this.getFilterValuesFromMenuItems(filterCategories, menuItems, guiTranslation);
        this.clearInvalidFilterValues(filters);
        return this.countAllFilterValues(filters, filteredItems);
    }

    clearInvalidFilterValues(filters: Filter[]) {
        // Remove any values that are no longer valid.
        this.filter = filters.reduce((validFilters: ActiveFilterMap, f) => {
            const filteredValues = this.filter[f.name];
            if (filteredValues) {
                const validValues: ActiveFilterValues = {};
                f.values.forEach(v => {
                    const valueUpper = v.value.toUpperCase();
                    if (filteredValues[valueUpper]) {
                        validValues[valueUpper] = v.value;
                    }
                });
                if (!_.isEmpty(validValues)) {
                    // eslint-disable-next-line no-param-reassign
                    validFilters[f.name] = validValues;
                }
            }
            return validFilters;
        }, {});
    }

    clear() {
        this.filter = {};
    }

    clearCategory(key: string) {
        if (this.filter[key]) {
            delete this.filter[key];
        }
    }

    clone(): ItemFilter {
        return new ItemFilter(this.locale, this.filter);
    }

    setTag(key: string, value: string, enabled: boolean) {
        let values = this.filter[key];
        if (!values) {
            if (!enabled) return;
            values = {};
            this.filter[key] = values;
        }

        if (enabled) {
            values[value.toUpperCase()] = value;
        } else {
            delete values[value.toUpperCase()];
            if (_.isEmpty(values)) {
                delete this.filter[key];
            }
        }
    }

    numActiveFilterValues() {
        return Object.values(this.filter).reduce((count, values) => count + _.size(values), 0);
    }

    combinedFilterValues(): { key: string; value: string }[] {
        const result: { key: string; value: string }[] = [];
        Object.entries(this.filter).forEach(([key, values]) =>
            Object.values(values).forEach(v => result.push({ key, value: v }))
        );
        return result;
    }

    getCurrentCategory(value: string): string | undefined {
        const valueUpper = value.toUpperCase();
        for (const key of Object.keys(this.filter)) {
            if (valueUpper in this.filter[key]) {
                return key;
            }
        }
        return undefined;
    }

    hasTagValue(key: string, value: string): boolean {
        const values = this.filter[key];
        if (!values) return false;
        return value.toUpperCase() in values;
    }

    hasFilterValue(value: string): boolean {
        const valueUpper = value.toUpperCase();
        return Object.values(this.filter).some((v: ActiveFilterValues) => {
            return valueUpper in v;
        });
    }

    filterItem(menuItem: Item, ignoredCategory: string | null): boolean {
        return Object.entries(this.filter).every(([key, values]) => {
            if (key === ignoredCategory) return true;

            const itemValues = menuItem.filterValues?.[key];
            if (!itemValues) {
                const uncategorizedName = getUncategorizedName(key);
                return this.hasTagValue(key, uncategorizedName);
            }

            return this.splitFilterValues(itemValues).some(s => s.toUpperCase() in values);
        });
    }

    filterItems(items: Item[]): Item[] {
        if (this.isEmpty()) return items;
        return items.filter((item: Item) => {
            return this.filterItem(item, null);
        });
    }

    isEmpty(): boolean {
        return _.isEmpty(this.filter) || Object.values(this.filter).every(v => _.isEmpty(v));
    }

    clearFilterValue = (value: string) => {
        const valueUpper = value.toUpperCase();
        Object.entries(this.filter).forEach(([key, activeValues]) => {
            // eslint-disable-next-line no-param-reassign
            delete activeValues[valueUpper];
            if (_.isEmpty(activeValues)) {
                delete this.filter[key];
            }
        });
    };

    getTagValues(type: string): string[] {
        const values = this.filter[type];
        return values ? Object.values(values) : [];
    }

    // Not used, but keep if we want to implement the same URL filtering logic as in SDS6.
    filterByUrlParams = (item: Item): boolean => {
        const searchParams = new URLSearchParams(window.location.search);
        const categoryFilter = this.splitUrlCategory(searchParams.get("category"));
        const articleFilter = this.splitUrlArticleNumber(searchParams.get("articles"));

        if (_.isEmpty(categoryFilter) && _.isEmpty(articleFilter)) {
            return false;
        }

        if (item.articleNumber) {
            const articleNumber = item.articleNumber.toUpperCase();
            if (articleFilter && articleFilter.some(f => f === articleNumber)) {
                return true;
            }
        }

        if (categoryFilter) {
            // TODO: Needs to be tested
            for (const key in categoryFilter) {
                if (Object.hasOwnProperty.call(categoryFilter, key)) {
                    const filterValues = categoryFilter[key];
                    const val = item.filterValues?.[key];
                    if (val) {
                        const itemValues = this.splitFilterValues(val).map(s => s.toUpperCase());
                        if (!itemValues.some(v => filterValues.has(v))) {
                            return false;
                        }
                    }
                }
            }
        }

        return false;
    };

    splitUrlArticleNumber = (articles: string | null): string[] => {
        if (articles === null) return [];

        return articles.toUpperCase().split(",");
    };

    splitUrlCategory = (category: string | null): { [key: string]: Set<string> } => {
        const result: { [key: string]: Set<string> } = {};
        if (category === null) return result;

        const tagPairs = category.split(";");
        tagPairs.forEach(value => {
            const valueStartPos = value.indexOf("(");
            const valueEndPos = value.lastIndexOf(")");
            if (valueStartPos === -1 || valueEndPos === -1) {
                return;
            }

            const tag = decodeURIComponent(value.substr(0, valueStartPos));
            if (!tag) {
                return;
            }

            const paranthesesContent = value.substring(valueStartPos + 1, valueEndPos);
            const values = this.splitFilterValues(decodeURIComponent(paranthesesContent)).map(s => s.toUpperCase());

            if (values.length === 0) {
                return;
            }

            result[tag] = new Set<string>(values);
        });

        return result;
    };

    splitFilterValues = (value: string): string[] => {
        const parts = value.split(/,/g);
        const result = [];
        const startWithQuote = /^\s*[“"”‘'’]/;
        const endWithQuote = /[“"”‘'’]\s*$/;
        const quotedParts: any[] = [];
        let isQuoted = false;

        parts.forEach(partsValue => {
            const p = partsValue;
            let val;
            if (isQuoted) {
                // If we're on a value starting with a quote, it has been separated by commas which
                // need to be joined together again when finding the end quote.
                // Since we're only looking for the end quote at the end of strings,
                // the value is actually also allowed to contain quotes in the middle of the string.
                if (!endWithQuote.test(p)) {
                    quotedParts.push(p);
                    return;
                }

                quotedParts.push(p.replace(endWithQuote, ""));
                val = quotedParts.join(",");
                quotedParts.length = 0;
                isQuoted = false;
            } else if (startWithQuote.test(p)) {
                if (!endWithQuote.test(p)) {
                    quotedParts.push(p.replace(startWithQuote, ""));
                    isQuoted = true;
                    return;
                }

                val = p.replace(startWithQuote, "").replace(endWithQuote, "");
            } else {
                // The value is not quoted, add it like normal.
                val = p.trim();
            }

            if (val !== "") {
                result.push(val);
            }
        });

        if (isQuoted) {
            // If we're on a quoted value, I guess we can add it just like we would if
            // the first value ends with, but doesn't start with a quote.
            result.push(quotedParts.join(","));
        }

        return result;
    };
}
